import { memo, useEffect, useMemo } from "react";
import { useAuth } from "@clerk/clerk-react";

import { Api, HttpClient } from "../openapi";
import type { ApiContextState } from "../contexts";
import { ApiContext } from "../contexts";

export interface ApiProviderProps {
  children?: React.ReactNode;
}

const api = new Api<void>(
  new HttpClient({
    baseURL: import.meta.env.VITE_API_BASE_URL,
    headers: { "Content-Type": "application/json" },
  }),
);

export const ApiProvider: React.FC<ApiProviderProps> = ({ children }) => {
  const { getToken } = useAuth();

  useEffect(() => {
    const httpClient = api.http.instance;

    const requestInterceptor = httpClient.interceptors.request.use(async (config) => {
      try {
        const token = await getToken({ template: import.meta.env.VITE_CLERK_AUTH_TEMPLATE });

        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      } catch (error) {
        console.error("Error fetching Clerk token:", error);
      }

      return config;
    });

    return () => {
      httpClient.interceptors.request.eject(requestInterceptor);
    };
  }, [getToken]);

  const contextValue = useMemo((): ApiContextState => ({ api }), []);

  return <ApiContext value={contextValue}>{children}</ApiContext>;
};

export default memo(ApiProvider);
